import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../lib/superAdminApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Copy, Check, Loader2, Building2, Play, Pause, LogIn, Trash2, Palette, Package, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminTenants() {
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCredentials, setShowCredentials] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmSlug, setConfirmSlug] = useState('');
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    superAdminApi.listTenants().then((r) => setTenants(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const impersonate = async (t) => {
    try {
      const { data } = await superAdminApi.impersonate(t.tenant_id, { tenant_id: t.tenant_id, duration_minutes: 30 });
      // Set the tenant user token and open a new tab into the tenant app
      localStorage.setItem('auth_token', data.token);
      toast.success(`A entrar como ${data.target_user.email} (${t.slug}). Sessão de ${new Date(data.impersonation_expires_at).toLocaleString('pt-PT')}`);
      window.open('/dashboard', '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha na impersonation');
    }
  };

  const suspend = async (t) => {
    await superAdminApi.suspendTenant(t.tenant_id); toast.success('Tenant suspenso'); load();
  };
  const activate = async (t) => {
    await superAdminApi.activateTenant(t.tenant_id); toast.success('Tenant reativado'); load();
  };
  const doDelete = async () => {
    try {
      await superAdminApi.deleteTenant(confirmDelete.tenant_id, confirmSlug);
      toast.success('Tenant eliminado permanentemente');
      setConfirmDelete(null); setConfirmSlug(''); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="sa-tenants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tenants</h1>
          <p className="text-slate-400 mt-1">Provisiona, suspende, elimina ou impersona qualquer tenant</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-400 hover:to-red-400 text-white" data-testid="sa-new-tenant-button">
          <Plus className="w-4 h-4 mr-2" /> Novo Tenant
        </Button>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader><CardTitle className="text-slate-100 flex items-center gap-2"><Building2 className="w-5 h-5" /> {tenants.length} tenants</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-400" /></div> : (
            <div className="divide-y divide-slate-800">
              {tenants.map((t) => (
                <div key={t.tenant_id} className="p-4" data-testid={`sa-tenants-row-${t.slug}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-100">{t.name}</span>
                        <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">/{t.slug}</Badge>
                        <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">{t.compliance_mode?.toUpperCase()}</Badge>
                        {t.status === 'active' && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">active</Badge>}
                        {t.status === 'suspended' && <Badge className="bg-red-500/20 text-red-300 border-red-500/30 border">suspenso</Badge>}
                        {t.status === 'provisioning' && <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 border">a provisionar…</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {t.contact_email && <span>📧 {t.contact_email} · </span>}
                        criado {new Date(t.created_at).toLocaleDateString('pt-PT')} · plan: {t.plan || 'default'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" onClick={() => impersonate(t)} data-testid={`sa-impersonate-${t.slug}`}>
                        <LogIn className="w-4 h-4 mr-1" /> Impersonar
                      </Button>
                      <Button size="sm" variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10" onClick={() => nav(`/super-admin/tenants/${t.tenant_id}/branding`)} data-testid={`sa-branding-${t.slug}`}>
                        <Palette className="w-4 h-4 mr-1" /> Branding
                      </Button>
                      <Button size="sm" variant="outline" className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10" onClick={() => nav(`/super-admin/tenants/${t.tenant_id}/templates`)} data-testid={`sa-templates-${t.slug}`}>
                        <Package className="w-4 h-4 mr-1" /> Templates
                      </Button>
                      <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10" onClick={() => {
                        const token = localStorage.getItem('super_admin_token');
                        const url = superAdminApi.previewHealthReport(t.tenant_id);
                        fetch(url, { headers: { 'Authorization': `Bearer ${token}` }})
                          .then(r => r.blob())
                          .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
                          .catch(() => toast.error('Falha a gerar PDF'));
                      }} data-testid={`sa-report-${t.slug}`}>
                        <FileText className="w-4 h-4 mr-1" /> Report
                      </Button>
                      {t.status === 'active' ? (
                        <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10" onClick={() => suspend(t)}>
                          <Pause className="w-4 h-4 mr-1" /> Suspender
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10" onClick={() => activate(t)}>
                          <Play className="w-4 h-4 mr-1" /> Ativar
                        </Button>
                      )}
                      {t.tenant_id !== 'default' && (
                        <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10" onClick={() => setConfirmDelete(t)} data-testid={`sa-delete-${t.slug}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <CreateTenantDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={(res) => { setShowCreate(false); setShowCredentials(res); load(); }} />

      {/* Credentials one-time dialog */}
      <Dialog open={!!showCredentials} onOpenChange={() => setShowCredentials(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">✅ Tenant criado</DialogTitle>
            <DialogDescription className="text-slate-400">Guarda a palavra-passe — só vai ser mostrada agora.</DialogDescription>
          </DialogHeader>
          {showCredentials && (
            <div className="space-y-3">
              <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
                <div className="text-xs text-slate-500 uppercase tracking-wide">Email do admin</div>
                <div className="font-mono text-sm text-slate-100 mt-1">{showCredentials.admin_user?.email}</div>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <div className="text-xs text-amber-400 uppercase tracking-wide">Palavra-passe inicial</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="font-mono text-sm text-amber-100 flex-1 break-all">{showCredentials.initial_password}</code>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(showCredentials.initial_password); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">O admin será obrigado a alterar a palavra-passe no primeiro login.</p>
            </div>
          )}
          <DialogFooter><Button onClick={() => setShowCredentials(null)}>Já guardei</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => { setConfirmDelete(null); setConfirmSlug(''); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-red-400">Eliminar tenant permanentemente</DialogTitle>
            <DialogDescription className="text-slate-400">
              Vais eliminar <b>{confirmDelete?.name}</b> e TODOS os seus dados. Não é reversível.
              Escreve o slug <code className="text-red-300">{confirmDelete?.slug}</code> para confirmar.
            </DialogDescription>
          </DialogHeader>
          <Input value={confirmSlug} onChange={(e) => setConfirmSlug(e.target.value)} placeholder={confirmDelete?.slug} className="bg-slate-950 border-slate-700" data-testid="sa-delete-confirm-slug" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmDelete(null); setConfirmSlug(''); }}>Cancelar</Button>
            <Button className="bg-red-500 hover:bg-red-400 text-white" disabled={confirmSlug !== confirmDelete?.slug} onClick={doDelete} data-testid="sa-delete-confirm-button">
              Eliminar permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CreateTenantDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: '', name: '', compliance_mode: 'both', contact_email: '',
    initial_admin_email: '', initial_admin_name: '', plan: 'starter',
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await superAdminApi.createTenant(form);
      toast.success(`Tenant "${form.slug}" criado`);
      onCreated(data);
      setForm({ slug: '', name: '', compliance_mode: 'both', contact_email: '', initial_admin_email: '', initial_admin_name: '', plan: 'starter' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao criar tenant');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo tenant</DialogTitle>
          <DialogDescription className="text-slate-400">Provisiona um novo cliente com admin, settings e catálogos default.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="sa-create-tenant-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300">Slug *</Label>
              <Input value={form.slug} onChange={set('slug')} placeholder="acme-corp" required className="bg-slate-950 border-slate-700" pattern="^[a-z0-9\-]+$" data-testid="sa-create-slug" />
            </div>
            <div>
              <Label className="text-slate-300">Nome *</Label>
              <Input value={form.name} onChange={set('name')} placeholder="ACME Corporation" required className="bg-slate-950 border-slate-700" data-testid="sa-create-name" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Modo de conformidade</Label>
            <Select value={form.compliance_mode} onValueChange={(v) => setForm({ ...form, compliance_mode: v })}>
              <SelectTrigger className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">NIS2 + AI Act</SelectItem>
                <SelectItem value="nis2_only">Só NIS2</SelectItem>
                <SelectItem value="ai_act_only">Só AI Act</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Email de contacto</Label>
            <Input type="email" value={form.contact_email} onChange={set('contact_email')} className="bg-slate-950 border-slate-700" />
          </div>
          <div className="pt-2 border-t border-slate-800">
            <p className="text-xs text-slate-500 mb-2">Admin inicial (será obrigado a mudar palavra-passe)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input type="email" value={form.initial_admin_email} onChange={set('initial_admin_email')} required className="bg-slate-950 border-slate-700" data-testid="sa-create-admin-email" />
              </div>
              <div>
                <Label className="text-slate-300">Nome *</Label>
                <Input value={form.initial_admin_name} onChange={set('initial_admin_name')} required className="bg-slate-950 border-slate-700" data-testid="sa-create-admin-name" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={loading} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="sa-create-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Provisionar tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
